import React, { lazy, Suspense, useCallback, useLayoutEffect, useState } from "react";
import { connect } from "react-redux";

import { ErrorBoundary } from "@sentry/react";

import { doesUserHaveAccessToAiFeatures } from "@app/store/iam/selectors";
import { RootState } from "@app/store/rootReducer";

// Prefetch, not plain lazy: the link is injected with the parent chunk, so entitled reps get
// the /me round trip as a head start instead of one fetch later.
const ChatWidget = lazy(() => import(/* webpackPrefetch: true */ "../ChatWidget"));

const AI_CHAT_ENABLED_CLASS = "ai-chat-enabled";

const ChatWidgetGate = ({ hasAccess }: ReturnType<typeof mapStateToProps>) => {
  const [chunkFailed, setChunkFailed] = useState(false);
  const handleChunkFailure = useCallback(() => setChunkFailed(true), []);

  // useLayoutEffect, not useEffect: on the SPA login transition HubSpot's launcher is already
  // painted, and a passive effect would leave both launchers on screen for a frame.
  //
  // Gated on chunkFailed too, so a rep whose chat chunk never loaded falls back to HubSpot's
  // launcher instead of being left with no chat at all — this class is what hides it.
  useLayoutEffect(() => {
    if (!hasAccess || chunkFailed) {
      return;
    }
    document.body.classList.add(AI_CHAT_ENABLED_CLASS);
    return () => {
      document.body.classList.remove(AI_CHAT_ENABLED_CLASS);
    };
  }, [chunkFailed, hasAccess]);

  if (!hasAccess) {
    return null;
  }

  // ErrorBoundary stops a failed chunk load from escaping to the app-level boundary and
  // replacing the whole CRM; Suspense's fallback={null} hides a late launcher, not a spinner.
  return (
    <ErrorBoundary fallback={<></>} onError={handleChunkFailure}>
      <Suspense fallback={null}>
        <ChatWidget />
      </Suspense>
    </ErrorBoundary>
  );
};

const mapStateToProps = (state: RootState) => ({
  hasAccess: doesUserHaveAccessToAiFeatures(state),
});

export default connect(mapStateToProps)(ChatWidgetGate);
